<template>
  <div class="main-layout" :class="{ 'is-mobile': isMobile }">
    <el-drawer
      v-if="isMobile"
      v-model="mobileSidebarVisible"
      custom-class="mobile-sidebar"
      direction="ltr"
      :with-header="false"
      size="78%"
    >
      <div class="sidebar-shell">
        <div class="sidebar-header">
          <div class="logo-section">
            <div class="logo-icon">
              <el-icon size="28"><TrendCharts /></el-icon>
            </div>
            <div class="logo-text">期现套利系统</div>
          </div>
          <el-button type="text" class="close-drawer" @click="closeSidebar">
            <el-icon><Close /></el-icon>
          </el-button>
        </div>

        <nav class="sidebar-nav">
          <SideMenu :collapsed="false" />
        </nav>

        <div class="sidebar-footer">
          <div class="system-info">
            <el-icon><Monitor /></el-icon>
            <span>期现套利系统 v3.0</span>
          </div>
          <el-button type="primary" @click="handleLogout" style="width: 100%">
            <el-icon><SwitchButton /></el-icon>
            退出系统
          </el-button>
        </div>
      </div>
    </el-drawer>

    <aside
      v-if="!isMobile"
      class="layout-sidebar"
      :class="{ 'is-collapsed': isCollapsed }"
      :style="{ width: sidebarWidth }"
    >
      <div class="sidebar-shell">
        <div class="sidebar-header">
          <div class="logo-section">
            <div class="logo-icon">
              <el-icon size="28"><TrendCharts /></el-icon>
            </div>
            <div v-show="!isCollapsed" class="logo-text">期现套利系统</div>
          </div>
          <el-button type="text" class="collapse-btn" @click="toggleCollapse">
            <el-icon><Fold v-if="!isCollapsed" /><Expand v-else /></el-icon>
          </el-button>
        </div>

        <nav class="sidebar-nav">
          <SideMenu :collapsed="isCollapsed" />
        </nav>

        <div v-show="!isCollapsed" class="sidebar-footer">
          <div class="system-info">
            <el-icon><Monitor /></el-icon>
            <span>期现套利系统 v3.0</span>
          </div>
          <el-button type="primary" @click="handleLogout" style="width: 100%">
            <el-icon><SwitchButton /></el-icon>
            退出系统
          </el-button>
        </div>
      </div>
    </aside>

    <div class="layout-main">
      <header class="layout-header">
        <div class="header-left">
          <el-button type="text" class="sidebar-trigger" @click="handleSidebarTrigger">
            <el-icon v-if="isMobile"><Menu /></el-icon>
            <template v-else>
              <el-icon v-if="isCollapsed">
                <Expand />
              </el-icon>
              <el-icon v-else>
                <Fold />
              </el-icon>
            </template>
          </el-button>
          <h1 class="page-title">{{ currentPageTitle }}</h1>
        </div>

        <div class="header-right">
          <el-dropdown trigger="click">
            <el-button type="text" class="user-dropdown">
              <el-avatar class="user-avatar" :size="32" :src="userAvatar">
                <el-icon><User /></el-icon>
              </el-avatar>
              <span class="username">{{ currentUser.username }}</span>
              <el-icon><ArrowDown /></el-icon>
            </el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="handleProfile">
                  <el-icon><User /></el-icon>个人中心
                </el-dropdown-item>
                <el-dropdown-item @click="handleSettings">
                  <el-icon><Setting /></el-icon>系统设置
                </el-dropdown-item>
                <el-dropdown-item divided @click="handleClearCache">
                  <el-icon><Delete /></el-icon>清空缓存
                </el-dropdown-item>
                <el-dropdown-item divided @click="handleLogout">
                  <el-icon><SwitchButton /></el-icon>退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </header>

      <main class="layout-content">
        <router-view v-slot="{ Component }">
          <transition name="fade-slide" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </main>
    </div>
    <NotificationCenter v-if="notificationCenterEnabled" />
  </div>
</template>

<script setup lang="ts">
import { logger } from "@/utils/logger";
import { computed, onUnmounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAppStore } from "@/stores/app";
import { useUserStore } from "@/stores/user";
import { basicInfoService } from "@/services";
import SideMenu from "@/components/Navigation/SideMenu.vue";
import NotificationCenter from "@/components/Common/NotificationCenter.vue";
import { useNotificationBus } from "@/composables/useNotificationBus";
import { useNotificationSocket } from "@/composables/useNotificationSocket";
import { isNotificationCenterRoute } from "@/utils/notificationCenterScope";
import { notificationDisplayPage } from "@/utils/notificationCenterScope";
import { useNotificationDisplayPolicy } from "@/composables/useNotificationDisplayPolicy";
import {
  TrendCharts,
  Fold,
  Expand,
  Menu,
  Monitor,
  SwitchButton,
  ArrowDown,
  User,
  Setting,
  Close,
} from "@element-plus/icons-vue";
import { Delete } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { TokenManager } from "@/utils/auth";

const router = useRouter();
const route = useRoute();
const appStore = useAppStore();
const userStore = useUserStore();
const notificationBus = useNotificationBus();
const notificationSocket = useNotificationSocket();
const notificationDisplayPolicy = useNotificationDisplayPolicy();

const isCollapsed = computed(() => appStore.sidebarCollapsed);
const isMobile = computed(() => appStore.isMobile);
const currentUser = computed(() => userStore.currentUser);
const currentPageTitle = computed(() => (route.meta?.title as string) || "首页");
const notificationCenterEnabled = computed(() => isNotificationCenterRoute(route.path));
const userAvatar = computed(() =>
  import.meta.env.VITE_OFFLINE_DEMO === "true"
    ? ""
    : `https://api.dicebear.com/7.x/avataaars/svg?seed=${currentUser.value.username || "guest"}`,
);

watch(
  [() => currentUser.value.username, () => route.path],
  ([username, path]) => {
    notificationBus.setUserScope(username || "");
    const page = notificationDisplayPage(path);
    if (page && username) {
      notificationSocket.start(username || "");
      notificationDisplayPolicy.start(page);
      return;
    }
    notificationDisplayPolicy.stop(!username);
    notificationSocket.stop();
    notificationBus.close();
  },
  { immediate: true },
);

onUnmounted(() => {
  notificationDisplayPolicy.stop();
  notificationSocket.stop();
  notificationBus.close();
});

const mobileSidebarVisible = computed({
  get: () => appStore.sidebarVisible,
  set: (value) => appStore.setSidebarVisible(value),
});

const sidebarWidth = computed(() =>
  isCollapsed.value ? "var(--sidebar-collapsed-width, 64px)" : "var(--sidebar-width, 240px)",
);

const toggleCollapse = () => {
  if (!isMobile.value) {
    appStore.toggleSidebarCollapse();
  }
};

const openSidebar = () => {
  if (!mobileSidebarVisible.value) {
    mobileSidebarVisible.value = true;
  }
};

const closeSidebar = () => {
  if (mobileSidebarVisible.value) {
    mobileSidebarVisible.value = false;
  }
};

const handleSidebarTrigger = () => {
  if (isMobile.value) {
    openSidebar();
  } else {
    toggleCollapse();
  }
};

watch(
  () => route.fullPath,
  () => {
    if (isMobile.value) {
      closeSidebar();
    }
  },
);

const handleLogout = () => {
  userStore.logout();
  router.push("/login");
};

const handleClearCache = async () => {
  try {
    await ElMessageBox.confirm("将清除本地缓存与登录凭据，并刷新页面。确认继续？", "清空缓存", {
      type: "warning",
      confirmButtonText: "确定",
      cancelButtonText: "取消",
    });
  } catch {
    return;
  }
  try {
    // 清除基础信息缓存（内存+localStorage）
    try {
      const userId = currentUser.value.username || currentUser.value.id;
      basicInfoService.clearCache(userId);
    } catch (e) {
      logger.warn("清理基础信息缓存失败:", e);
    }
    // 清除本地存储
    localStorage.clear();
    sessionStorage.clear();
    // 清除Cookie中存放的用户名与token
    TokenManager.clearCredentials();
    // 可选：尝试清除 CacheStorage（若浏览器支持且有PWA缓存）
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    ElMessage.success("已清空缓存，即将刷新");
  } catch (e) {
    logger.warn("清空缓存时发生错误:", e);
  } finally {
    // 刷新页面，回到登录
    window.location.href = "/login";
  }
};

const handleProfile = () => {
  router.push("/profile");
};

const handleSettings = () => {
  router.push("/settings");
};
</script>

<style lang="scss" scoped>
.main-layout {
  display: flex;
  min-height: 100vh;
  width: 100%;
  background: var(--bg-secondary);
  color: var(--text-primary);
  position: relative;

  &.is-mobile {
    flex-direction: column;
  }
}

.layout-sidebar {
  background: var(--bg-primary);
  border-right: 1px solid var(--border-light);
  transition:
    width var(--animation-duration-slow) ease,
    box-shadow var(--animation-duration-slow) ease;
  display: flex;
  flex-direction: column;
  height: 100vh;
  position: sticky;
  top: 0;
  z-index: 10;
  overflow: hidden;
  width: var(--sidebar-width, 240px);
  min-width: var(--sidebar-width, 240px);
  flex: 0 0 auto;

  &.is-collapsed {
    width: var(--sidebar-collapsed-width, 64px);
    min-width: var(--sidebar-collapsed-width, 64px);

    .logo-text {
      opacity: 0;
      pointer-events: none;
    }

    .logo-section {
      justify-content: center;
    }

    .sidebar-footer {
      display: none;
    }
  }
}

.sidebar-shell {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sidebar-header {
  height: var(--header-height);
  padding: 0 var(--spacing-lg);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--border-light);

  .logo-section {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    min-width: 0;
  }

  .logo-text {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: opacity var(--animation-duration-slow) ease;
  }

  .collapse-btn,
  .close-drawer {
    color: var(--text-secondary);
    padding: 6px;
    border-radius: var(--border-radius-base);
    transition: all var(--animation-duration-base) ease;

    &:hover {
      background: var(--bg-secondary);
      color: var(--primary-color);
    }
  }
}

.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-sm) 0;
}

.sidebar-footer {
  padding: var(--spacing-lg);
  border-top: 1px solid var(--border-light);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);

  .system-info {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    color: var(--text-tertiary);
    font-size: 12px;
  }
}

.layout-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--bg-secondary);
}

.layout-header {
  height: var(--header-height);
  padding: 0 var(--spacing-2xl);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-light);
  position: sticky;
  top: 0;
  z-index: 5;

  .header-left {
    display: flex;
    align-items: center;
    gap: var(--spacing-lg);

    .sidebar-trigger {
      color: var(--text-secondary);
      padding: 8px;
      border-radius: var(--border-radius-base);
      transition: all var(--animation-duration-base) ease;

      &:hover {
        color: var(--primary-color);
        background: var(--bg-secondary);
      }

      .el-icon {
        font-size: 20px;
      }
    }

    .page-title {
      margin: 0;
      font-size: var(--font-size-xl);
      font-weight: 600;
      color: var(--text-primary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: var(--spacing-lg);

    .user-dropdown {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
      padding: 6px 12px;
      border-radius: 999px;
      transition: background var(--animation-duration-base) ease;

      &:hover {
        background: var(--bg-secondary);
      }

      .user-avatar {
        background: var(--bg-secondary);
      }

      .username {
        font-weight: 500;
        color: var(--text-primary);
        max-width: 120px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}

.layout-content {
  flex: 1;
  min-height: 0;
  padding: var(--spacing-3xl);
  background: var(--bg-secondary);
  overflow-y: auto;
  overflow-x: hidden;
}

@media (max-width: 1200px) {
  .layout-header {
    padding: 0 var(--spacing-xl);
  }

  .layout-content {
    padding: var(--spacing-2xl);
  }
}

@media (max-width: 768px) {
  .main-layout.is-mobile {
    .layout-header {
      height: 56px;
      padding: 0 var(--spacing-lg);

      .header-right .username {
        display: none;
      }
    }

    .layout-content {
      padding: var(--spacing-xl);
    }
  }
}

@media (max-width: 480px) {
  .layout-content {
    padding: var(--spacing-lg);
  }
}

.mobile-sidebar {
  .el-drawer__body {
    padding: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .sidebar-shell {
    height: 100%;
  }

  .sidebar-footer {
    padding-bottom: calc(var(--spacing-lg) + env(safe-area-inset-bottom, 0));
  }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all var(--animation-duration-base) ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateX(10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}
</style>
